import { useState, useEffect } from 'react';
import { useQuery } from 'urql';

import type { InstagramCreatorFilter } from '@frontend/gql/social/graphql';
import { suggestInstagramCreatorFilter } from '../graphql';

interface UseSuggestedInstagramCreatorFilterReturn {
  filter: InstagramCreatorFilter | null;
  promptUsedForSuggestion: string | null;
  isUserEnteredPrompt: boolean;
  isLoading: boolean;
  error: Error | null;
  generateFilter: (prompt: string, isUserEntered?: boolean) => void;
  clearSuggestedFilter: () => void;
}

export const useSuggestedInstagramCreatorFilter = (): UseSuggestedInstagramCreatorFilterReturn => {
  const [prompt, setPrompt] = useState<string | null>(null);
  const [isUserEnteredPrompt, setIsUserEnteredPrompt] = useState<boolean>(false);
  const [filter, setFilter] = useState<InstagramCreatorFilter | null>(null);
  const [promptUsedForSuggestion, setPromptUsedForSuggestion] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const [{ data, fetching, error: queryError }] = useQuery({
    query: suggestInstagramCreatorFilter,
    variables: { prompt: prompt || '' },
    pause: !prompt, // Only run when prompt is provided
  });

  useEffect(() => {
    if (data?.suggestInstagramCreatorFilter?.filter) {
      setFilter(data.suggestInstagramCreatorFilter.filter);
      // Only set promptUsedForSuggestion if it was user-entered
      // This ensures system-generated prompts don't get added to URL
      setPromptUsedForSuggestion(isUserEnteredPrompt ? prompt : null);
      setError(null);
    } else if (queryError) {
      console.error('Error getting suggested filter:', queryError);
      setError(new Error('Failed to generate suggested filter'));
      setFilter(null);
      setPromptUsedForSuggestion(null);
    }
  }, [data, queryError, prompt, isUserEnteredPrompt]);

  const generateFilter = (newPrompt: string, isUserEntered = true) => {
    setError(null);
    setPrompt(newPrompt);
    setIsUserEnteredPrompt(isUserEntered);
  };

  const clearSuggestedFilter = () => {
    setFilter(null);
    setPromptUsedForSuggestion(null);
    setError(null);
  };

  return {
    filter,
    promptUsedForSuggestion,
    isUserEnteredPrompt,
    isLoading: fetching,
    error,
    generateFilter,
    clearSuggestedFilter,
  };
};
